// WHAT: Withings sync button component with smart and force sync variants
// WHY:  Provides UI for triggering Withings data synchronization with progress indicators and error handling
// DOMAIN: Health → Withings → Data Synchronization
// DEPENDENCIES: WithingsSmartSyncService, useGlobalProgress context, debugLog utility
// RISKS:
//   - NOTE: Sync operation is asynchronous - may fail silently if service is unavailable
//   - NOTE: Progress indicators use multiple state variables - may cause UI flickering
//   - WARN: Error handling only logs to debug - user may not see sync failures
//   - NOTE: Callback execution after sync may fail if component unmounts during operation

import { type FC, useState } from "react";

import { Button } from "@/components/ui";
import { debugLog } from "@/domains/logging/utils/debugLog";
import { useGlobalProgress } from "@/shared/contexts";

import { WithingsSmartSyncService } from "../../services/withingsSmartSync";
import { WithingsSyncLoader } from "./WithingsLoader";

interface WithingsSyncButtonProperties {
  className?: string;
  onSyncComplete?: () => void;
  variant?: "force" | "smart";
}

/**
 * WHAT:
 *   Button component for triggering Withings data synchronization with progress indicators.
 *
 * WHY:
 *   Provides user interface for syncing Withings health data with two sync strategies:
 *   smart sync (incremental) and force sync (full refresh).
 *
 * PROPS:
 *   - onSyncComplete?: () => void — Optional callback executed after successful sync
 *   - variant?: 'smart' | 'force' — Sync strategy: smart (incremental) or force (full)
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Shows loading state with spinner icon during sync operation
 *   - Displays page-level loader overlay during sync
 *   - Integrates with global progress context for footer progress indicator
 *   - Logs all sync operations to debug system
 *   - Executes optional callback after successful sync completion
 *   - Button text changes based on variant and loading state
 *
 * RISKS:
 *   - NOTE: Sync operation is asynchronous - may fail silently if service is unavailable
 *   - NOTE: Progress indicators use multiple state variables - may cause UI flickering
 *   - WARN: Error handling only logs to debug - user may not see sync failures
 *   - NOTE: Callback execution after sync may fail if component unmounts during operation
 *   - WARN: No retry mechanism - failed syncs require manual retry
 *
 * @param root0
 *
 * @param root0.onSyncComplete
 *
 * @param root0.variant
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Withings sync button component
 *
 */
export const WithingsSyncButton: FC<WithingsSyncButtonProperties> = ({
  className = "",
  onSyncComplete,
  variant = "smart",
}) => {
  const { endSync, startSync } = useGlobalProgress();
  const [loading, setLoading] = useState(false);
  const [showPageLoader, setShowPageLoader] = useState(false);

  /**
   * WHAT:
   *   Handles button click to initiate Withings synchronization operation.
   *
   * WHY:
   *   Centralizes sync logic with proper error handling, progress tracking, and callback execution.
   *
   * SIDE EFFECTS:
   *   - Sets loading state to true
   *   - Shows page loader overlay
   *   - Activates global progress indicator
   *   - Calls WithingsSmartSyncService based on variant
   *   - Executes onSyncComplete callback if provided
   *   - Logs all operations to debug system
   *
   * RISKS:
   *   - NOTE: Errors are caught but only logged - no user-visible error feedback
   *   - WARN: Callback execution happens in try block - may not execute on error
   *   - NOTE: Multiple state updates may cause race conditions
   */
  const handleClick = async () => {
    debugLog("info", "health", "Withings sync button clicked", {
      file: "WithingsSyncButton.tsx",
      function: "handleClick",
      module: "withings",
      variant,
    });

    setLoading(true);
    setShowPageLoader(true); // PageLoader anzeigen
    startSync(); // Footer-Loader aktivieren

    try {
      debugLog("info", "health", `Calling ${variant} sync command`, {
        file: "WithingsSyncButton.tsx",
        function: "handleClick",
        module: "withings",
        variant,
      });

      const result =
        variant === "smart"
          ? await WithingsSmartSyncService.smartSync()
          : await WithingsSmartSyncService.forceFullSync();

      debugLog("info", "health", "Withings sync command completed", {
        file: "WithingsSyncButton.tsx",
        function: "handleClick",
        module: "withings",
        result: {
          newMeasurements: result.newMeasurements,
          success: result.success,
          totalMeasurements: result.totalMeasurements,
        },
      });

      // Callback für UI-Update
      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      debugLog("error", "health", `Withings sync command failed: ${errorMessage}`, {
        file: "WithingsSyncButton.tsx",
        function: "handleClick",
        module: "withings",
        variant,
      });
    } finally {
      setLoading(false);
      setShowPageLoader(false); // PageLoader ausblenden
      endSync(); // Footer-Loader deaktivieren
    }
  };

  /**
   * WHAT:
   *   Returns localized button text based on variant and loading state.
   *
   * WHY:
   *   Provides user-friendly button labels that reflect current operation state.
   *
   * OUTPUT:
   *   German button text string indicating sync type and state.
   *
   * @returns {string} German button text string
   *
   */
  const getButtonText = () => {
    if (loading) {
      return variant === "smart" ? "Synchronisiere..." : "Vollständig synchronisiere...";
    }
    return variant === "smart" ? "Smart Sync" : "Force Sync";
  };

  /**
   * WHAT:
   *   Returns appropriate icon element based on loading state.
   *
   * WHY:
   *   Provides visual feedback for button state (loading spinner vs sync icon).
   *
   * OUTPUT:
   *   React element - either loading spinner or emoji sync icon.
   *
   * RISKS:
   *   - NOTE: Uses emoji for icon - may not render consistently across platforms
   *
   * @returns {JSX.Element} Button icon element
   *
   */
  const getButtonIcon = () => {
    if (loading) {
      return (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      );
    }
    return <span className="text-sm">🔄</span>;
  };

  return (
    <>
      {showPageLoader ? (
        <WithingsSyncLoader
          message={variant === "smart" ? "Smart Sync läuft..." : "Force Sync läuft..."}
        />
      ) : null}

      <Button
        className={`flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50 ${className}`}
        disabled={loading}
        onClick={() => {
          void handleClick();
        }}
        title={`Withings ${variant === "smart" ? "Smart" : "Force"} Sync`}
      >
        {getButtonIcon()}
        <span>{getButtonText()}</span>
      </Button>
    </>
  );
};
